import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { format, parseISO, subDays } from 'date-fns';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowPathIcon,
  BanknotesIcon,
  CalendarIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  ScaleIcon,
} from '@heroicons/react/24/outline';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import DateQuickPresets from '../../components/DateQuickPresets';

const buildInitialFilters = (searchParams) => {
  const hoje = format(new Date(), 'yyyy-MM-dd');
  return {
    data_inicio: searchParams.get('data_inicio') || format(subDays(new Date(), 29), 'yyyy-MM-dd'),
    data_fim: searchParams.get('data_fim') || hoje,
    conta_bancaria_id: searchParams.get('conta_bancaria_id') || '',
  };
};

const DATASET_DEFAULT = {
  contas_selecionadas: [],
  resumo: {
    total_contas: 0,
    saldo_base: 0,
    saldo_anterior: 0,
    entradas: 0,
    saidas: 0,
    saldo_atual: 0,
    movimentos: 0,
  },
  serie: [],
};

const ConferenciaSaldo = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtros, setFiltros] = useState(() => buildInitialFilters(searchParams));
  const [contasBancarias, setContasBancarias] = useState([]);
  const [dados, setDados] = useState(DATASET_DEFAULT);
  const [carregando, setCarregando] = useState(false);

  const formatarValor = (value) => toNumber(value).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  function toNumber(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  const carregarContas = async () => {
    try {
      const response = await axios.get('/api/contas-bancarias');
      setContasBancarias(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar contas para conferencia de saldo:', error);
      toast.error('Erro ao carregar contas bancarias');
    }
  };

  const carregarDados = async () => {
    setCarregando(true);
    try {
      const response = await axios.get('/api/conferencia-saldo', { params: filtros });
      const payload = response.data || {};
      setDados({
        contas_selecionadas: Array.isArray(payload.contas_selecionadas) ? payload.contas_selecionadas : [],
        resumo: {
          ...DATASET_DEFAULT.resumo,
          ...(payload.resumo || {}),
        },
        serie: Array.isArray(payload.serie)
          ? payload.serie.map((item) => ({
              ...item,
              entradas: toNumber(item?.entradas),
              saidas: toNumber(item?.saidas),
              saldo_anterior: toNumber(item?.saldo_anterior),
              saldo_atual: toNumber(item?.saldo_atual),
              movimentos: Number(item?.movimentos || 0),
            }))
          : [],
      });
    } catch (error) {
      console.error('Erro ao carregar conferencia de saldo:', error);
      toast.error(error.response?.data?.error || 'Erro ao carregar conferencia de saldo');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregarContas();
  }, []);

  useEffect(() => {
    if (filtros.conta_bancaria_id || contasBancarias.length === 0) return;
    const contaPreferida = contasBancarias.find((conta) => conta.ativa)?.id || contasBancarias[0]?.id || 'todas';
    setFiltros((old) => ({ ...old, conta_bancaria_id: contaPreferida }));
  }, [contasBancarias, filtros.conta_bancaria_id]);

  useEffect(() => {
    const nextParams = new URLSearchParams();
    if (filtros.data_inicio) nextParams.set('data_inicio', filtros.data_inicio);
    if (filtros.data_fim) nextParams.set('data_fim', filtros.data_fim);
    if (filtros.conta_bancaria_id) nextParams.set('conta_bancaria_id', filtros.conta_bancaria_id);
    setSearchParams(nextParams, { replace: true });
  }, [filtros, setSearchParams]);

  useEffect(() => {
    carregarDados();
  }, [filtros]);

  const contaSelecionada = useMemo(() => {
    if (filtros.conta_bancaria_id === 'todas') {
      return null;
    }
    return contasBancarias.find((conta) => conta.id === filtros.conta_bancaria_id)
      || dados.contas_selecionadas.find((conta) => conta.id === filtros.conta_bancaria_id)
      || null;
  }, [contasBancarias, dados.contas_selecionadas, filtros.conta_bancaria_id]);

  const chartData = useMemo(
    () => (dados.serie || []).map((item) => ({
      ...item,
      label: format(parseISO(item.dia), 'dd/MM'),
    })),
    [dados.serie]
  );

  const escopoLabel = filtros.conta_bancaria_id === 'todas'
    ? `${dados.resumo.total_contas} conta(s) no escopo`
    : (contaSelecionada ? `${contaSelecionada.banco} - ${contaSelecionada.conta}` : 'Conta selecionada');

  return (
    <div className="animate-fade-in">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Conferencia de Saldo</h1>
          <p className="text-gray-600">Saldo anterior, entradas, saidas e saldo atual para conciliacao por conta.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/lancamentos" className="btn-secondary">
            Lancamentos
          </Link>
          <Link to="/relatorios" className="btn-secondary">
            Central de Relatorios
          </Link>
          <button type="button" onClick={carregarDados} className="btn-secondary">
            <ArrowPathIcon className="h-5 w-5 mr-2" />
            Atualizar
          </button>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-body">
          <DateQuickPresets
            mode="range"
            startDate={filtros.data_inicio}
            endDate={filtros.data_fim}
            onSelect={({ startDate, endDate }) => setFiltros((old) => ({ ...old, data_inicio: startDate, data_fim: endDate }))}
            className="mb-4"
          />

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <div>
              <label className="form-label">
                <CalendarIcon className="h-4 w-4 inline mr-1" />
                Data Inicio
              </label>
              <input
                type="date"
                className="form-input"
                value={filtros.data_inicio}
                onChange={(e) => setFiltros((old) => ({ ...old, data_inicio: e.target.value }))}
              />
            </div>
            <div>
              <label className="form-label">
                <CalendarIcon className="h-4 w-4 inline mr-1" />
                Data Fim
              </label>
              <input
                type="date"
                className="form-input"
                value={filtros.data_fim}
                onChange={(e) => setFiltros((old) => ({ ...old, data_fim: e.target.value }))}
              />
            </div>
            <div className="md:col-span-2">
              <label className="form-label">Conta Bancaria</label>
              <select
                className="form-select"
                value={filtros.conta_bancaria_id}
                onChange={(e) => setFiltros((old) => ({ ...old, conta_bancaria_id: e.target.value }))}
              >
                <option value="todas">Todas as contas</option>
                {contasBancarias.map((conta) => (
                  <option key={conta.id} value={conta.id}>
                    {conta.banco} - {conta.conta} ({conta.tipo})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <div className="w-full rounded-lg border border-primary-100 bg-primary-50 px-3 py-2 text-sm text-primary-900">
                Somente lancamentos confirmados entram na conciliacao.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 mb-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-primary-100">
                <BanknotesIcon className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-4 min-w-0">
                <p className="text-sm text-gray-500">Escopo</p>
                <p className="text-sm font-semibold text-gray-900 truncate">{escopoLabel}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Saldo Anterior</p>
            <p className={`text-2xl font-bold ${dados.resumo.saldo_anterior >= 0 ? 'text-success-700' : 'text-danger-700'}`}>
              R$ {formatarValor(dados.resumo.saldo_anterior)}
            </p>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Entradas</p>
            <p className="text-2xl font-bold text-success-700">R$ {formatarValor(dados.resumo.entradas)}</p>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Saidas</p>
            <p className="text-2xl font-bold text-danger-700">R$ {formatarValor(dados.resumo.saidas)}</p>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Saldo Atual</p>
            <p className={`text-2xl font-bold ${dados.resumo.saldo_atual >= 0 ? 'text-success-700' : 'text-danger-700'}`}>
              R$ {formatarValor(dados.resumo.saldo_atual)}
            </p>
          </div>
        </div>
      </div>

      <div className="mb-6 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-600">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
          <span>
            Base cadastral inicial: <strong>R$ {formatarValor(dados.resumo.saldo_base)}</strong> | Movimentos no periodo: <strong>{dados.resumo.movimentos}</strong>
          </span>
          {filtros.conta_bancaria_id === 'todas' && (
            <span>
              Transferencias internas entre contas selecionadas nao entram como entrada ou saida.
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.8fr)_minmax(320px,1fr)] gap-6 mb-6">
        <div className="card">
          <div className="card-header flex justify-between items-center">
            <h3 className="text-lg font-semibold">Evolucao Diaria</h3>
            {carregando && <div className="loader h-5 w-5" />}
          </div>
          <div className="card-body">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" stroke="#6b7280" fontSize={12} />
                  <YAxis stroke="#6b7280" fontSize={12} tickFormatter={(value) => `R$ ${(toNumber(value) / 1000).toFixed(0)}k`} />
                  <Tooltip
                    formatter={(value, name) => {
                      const labels = {
                        entradas: 'Entradas',
                        saidas: 'Saidas',
                        saldo_atual: 'Saldo Atual',
                      };
                      return [`R$ ${formatarValor(value)}`, labels[name] || name];
                    }}
                  />
                  <Legend />
                  <Bar dataKey="entradas" name="Entradas" fill="#16a34a" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="saidas" name="Saidas" fill="#dc2626" radius={[4, 4, 0, 0]} />
                  <Line type="monotone" dataKey="saldo_atual" name="Saldo Atual" stroke="#2563eb" strokeWidth={2.5} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold">Leitura da Conferencia</h3>
          </div>
          <div className="card-body space-y-4">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-center gap-3">
                <ScaleIcon className="h-5 w-5 text-primary-600" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Saldo anterior</p>
                  <p className="text-xs text-gray-500">Posicao imediatamente antes da data inicial selecionada.</p>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-center gap-3">
                <CurrencyDollarIcon className="h-5 w-5 text-success-600" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Entradas e saidas</p>
                  <p className="text-xs text-gray-500">Receitas confirmadas e transferencias que entram ou saem do escopo.</p>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-center gap-3">
                <ChartBarIcon className="h-5 w-5 text-primary-600" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Saldo atual</p>
                  <p className="text-xs text-gray-500">Resultado acumulado ate a data final do filtro.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold">Detalhamento Diario</h3>
            <p className="text-xs text-gray-500">Use esta grade para conferir o saldo e localizar dias com divergencia.</p>
          </div>
          {carregando && <div className="loader h-5 w-5" />}
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Saldo Anterior</th>
                  <th>Entradas</th>
                  <th>Saidas</th>
                  <th>Saldo Atual</th>
                  <th>Movimentos</th>
                </tr>
              </thead>
              <tbody>
                {dados.serie.map((item) => (
                  <tr key={item.dia}>
                    <td>{format(parseISO(item.dia), 'dd/MM/yyyy')}</td>
                    <td className={item.saldo_anterior >= 0 ? 'text-success-700 font-medium' : 'text-danger-700 font-medium'}>
                      R$ {formatarValor(item.saldo_anterior)}
                    </td>
                    <td className="text-success-700 font-medium">R$ {formatarValor(item.entradas)}</td>
                    <td className="text-danger-700 font-medium">R$ {formatarValor(item.saidas)}</td>
                    <td className={item.saldo_atual >= 0 ? 'text-success-700 font-semibold' : 'text-danger-700 font-semibold'}>
                      R$ {formatarValor(item.saldo_atual)}
                    </td>
                    <td>{item.movimentos}</td>
                  </tr>
                ))}
                {dados.serie.length === 0 && !carregando && (
                  <tr>
                    <td colSpan={6} className="text-center text-gray-500 py-8">
                      Nenhum dado encontrado para o periodo selecionado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConferenciaSaldo;
